/**
 * TestimonialsSection Component
 * Displays customer testimonials and reviews for Siyaram Jewellers
 */

import React from 'react';

interface Testimonial {
  id: string;
  rating: number;
  quote: string;
  customerName: string;
  customerInitials: string;
  verified: boolean;
}

export const TestimonialsSection: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      id: '1',
      rating: 5,
      quote: 'The attention to detail in their custom designs is unmatched. I had a necklace made for my wedding, and it truly exceeded my expectations.',
      customerName: 'Ananya Kapoor',
      customerInitials: 'AK',
      verified: true,
    },
    {
      id: '2',
      rating: 5,
      quote: "I've been a regular here for 10 years. Their gold rates are always competitive and the transparency they provide is very reassuring.",
      customerName: 'Rajesh Sharma',
      customerInitials: 'RS',
      verified: true,
    },
    {
      id: '3',
      rating: 5,
      quote: 'Superb customer service. They helped me pick the perfect anniversary gift and even handled the resizing within a day. Highly recommend!',
      customerName: 'Meera Patel',
      customerInitials: 'MP',
      verified: true,
    },
  ];

  const renderStars = (rating: number) => {
    return (
      <div className="flex space-x-1 mb-4">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`w-5 h-5 ${index < rating ? 'text-yellow-500' : 'text-gray-600'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <section id="testimonials" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-12">
          <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-4">
            Client Stories
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white">
            Reflections of Satisfaction
          </h2>
        </div>

        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm relative"
            >
              {/* Stars */}
              {renderStars(testimonial.rating)}

              {/* Quote */}
              <p className="text-gray-400 italic text-base leading-relaxed mb-6 relative z-10">
                "{testimonial.quote}"
              </p>

              {/* Large Quote Mark */}
              <div className="absolute bottom-8 right-8 text-yellow-500/20 text-8xl font-serif leading-none">
                "
              </div>

              {/* Customer Info */}
              <div className="flex items-center space-x-3 relative z-10">
                {/* Avatar */}
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {testimonial.customerInitials}
                </div>

                {/* Name and Verification */}
                <div>
                  <p className="text-white font-semibold">{testimonial.customerName}</p>
                  {testimonial.verified && (
                    <p className="text-gray-500 text-xs">Verified Customer</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
